import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useCallback, useEffect } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router';

import { DialogKey } from '@sorare/core/src/components/navigation/WithDialogs';
import {
  ANY_SPORT_MARKET_SHOP_AUCTIONS,
  LANDING,
} from '@sorare/core/src/constants/__generated__/routes';
import { currentUserConversionCredits } from '@sorare/core/src/contexts/currentUser/queries';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import { useMutation } from '@sorare/core/src/hooks/graphql/useMutation';
import { useGenerateDialogPath } from '@sorare/core/src/hooks/navigation/useGenerateDialogPath';
import { generateSportPath } from '@sorare/core/src/lib/routing/generateSportPath';

import type {
  ClaimConversionCreditMutation,
  ClaimConversionCreditMutationVariables,
} from './__generated__/page.graphql.ts';

const CLAIM_CONVERSION_CREDIT_MUTATION = gql`
  mutation ClaimConversionCreditMutation($input: claimConversionCreditInput!) {
    claimConversionCredit(input: $input) {
      conversionCredit {
        id
        sport
      }
      currentUser {
        slug
        ...CurrentUserProvider_sportConversionCredits
      }
      errors {
        path
        message
        code
      }
    }
  }
  ${currentUserConversionCredits}
` as TypedDocumentNode<
  ClaimConversionCreditMutation,
  ClaimConversionCreditMutationVariables
>;

const PromoClaimPage = () => {
  const generateDialogPath = useGenerateDialogPath();
  const [mutate] = useMutation(CLAIM_CONVERSION_CREDIT_MUTATION, {
    showErrorsWithSnackNotification: true,
  });
  const { sport } = useSportContext();
  const { code } = useParams();
  const navigate = useNavigate();

  const claimConversionCredit = useCallback(async () => {
    const { data } = await mutate({
      variables: {
        input: {
          promoCode: code,
        },
      },
    });

    const conversionCredit = data?.claimConversionCredit?.conversionCredit;
    const ccSport = conversionCredit?.sport || sport;

    const basePath =
      generateSportPath(ANY_SPORT_MARKET_SHOP_AUCTIONS, { sport: ccSport }) ||
      LANDING;
    return navigate(
      data?.claimConversionCredit?.conversionCredit?.id
        ? generateDialogPath(
            DialogKey.claimedConversionCredit,
            idFromObject(data?.claimConversionCredit?.conversionCredit.id),
            {
              basePath,
            }
          )
        : basePath
    );
  }, [code, mutate, navigate, generateDialogPath, sport]);

  useEffect(() => {
    if (code) {
      claimConversionCredit();
    }
  }, [claimConversionCredit, code]);

  if (!code) {
    return <Navigate to={LANDING} replace />;
  }

  return null;
};

export default PromoClaimPage;
